import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  API, CODE_KEY, AGENT_KEY, ROLE_KEY, INACTIVITE_MS, MSG_INACTIVITE,
  entetes, estNonAutorise, lireStockage, ecrireStockage, definirMotifDeconnexion,
} from "./mobile/outils";
import EcranLogin from "./mobile/EcranLogin";
import EcranRecherche from "./mobile/EcranRecherche";
import EcranFiche from "./mobile/EcranFiche";
import EcranScanner from "./mobile/EcranScanner";
import EcranConsentement from "./mobile/EcranConsentement";
import EcranMessages, { EcranConversation } from "./mobile/EcranMessages";
import EcranCaisse from "./mobile/EcranCaisse";

/*
  Navigation : l'écran Recherche reste TOUJOURS monté (recherche, filtre, défilement et
  données conservés). Les autres écrans forment une pile d'écrans superposés (fixed inset-0) :
    [ fiche | scanner | consentement | signature ]...
  Chaque ouverture ajoute une entrée d'historique (history.pushState) : le bouton retour
  d'Android (ou le geste retour) déclenche popstate, qui ramène la pile à la profondeur
  enregistrée dans l'entrée. Les boutons « Retour » de l'interface appellent history.back(),
  il n'y a donc qu'un seul chemin pour dépiler.
  La signature est une entrée de la pile (le retour Android la ferme) mais elle est affichée
  par l'écran Consentement situé juste en dessous, qui garde ainsi l'aperçu signé.
*/

let compteurEcrans = 0;
const SANS_LIMITE_KEY = "khaldoun_sans_limite";

// Le jeton d'une session Windows porte app = "win" (lu sans vérification : simple réglage local,
// le serveur reste seul juge).
function sessionWindows(jeton) {
  try {
    const partie = String(jeton || "").split(".")[1] || "";
    const json = atob(partie.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(partie.length / 4) * 4, "="));
    return JSON.parse(json).app === "win";
  } catch (e) {
    return false;
  }
}

function App() {
  // 2.3.1 : lien « Application web » du logiciel (?entree=…) -> on ignore l'ancienne session, l'écran de
  // connexion échange le code contre une session Windows.
  const [code, setCode] = useState(() =>
    new URLSearchParams(window.location.search).get("entree") ? "" : lireStockage(CODE_KEY)
  );
  const [agent, setAgent] = useState(() => lireStockage(AGENT_KEY));
  const [role, setRole] = useState(() => lireStockage(ROLE_KEY));
  const [pile, setPile] = useState([]);
  const pileRef = useRef([]);
  // 2.4.1 : bouton « Messages WhatsApp » du logiciel (?entree=…&ecran=messages) -> écran Messages après connexion
  const ecranDemande = useRef(new URLSearchParams(window.location.search).get("ecran") || "");

  const majPile = useCallback((suivante) => {
    pileRef.current = suivante;
    setPile(suivante);
  }, []);

  useEffect(() => {
    // Au rechargement, une ancienne entrée « écran ouvert » ne doit rien rouvrir
    if (window.history.state && window.history.state.khaldoun) window.history.replaceState(null, "");
    const surRetour = (e) => {
      const profondeur = (e.state && e.state.khaldoun) || 0;
      if (pileRef.current.length > profondeur) majPile(pileRef.current.slice(0, profondeur));
    };
    window.addEventListener("popstate", surRetour);
    return () => window.removeEventListener("popstate", surRetour);
  }, [majPile]);

  const ouvrir = useCallback(
    (ecran) => {
      // Double appui : on n'empile pas deux fois le même écran
      const identite = (x) => `${x.type}:${x.id || x.numero || (x.personne && x.personne.id) || ""}`;
      const dessus = pileRef.current[pileRef.current.length - 1];
      if (dessus && identite(dessus) === identite(ecran)) return;
      compteurEcrans += 1;
      const suivante = [...pileRef.current, { ...ecran, cle: compteurEcrans }];
      window.history.pushState({ khaldoun: suivante.length }, "");
      majPile(suivante);
    },
    [majPile]
  );

  const retour = useCallback(() => {
    if (pileRef.current.length) window.history.back(); // -> popstate -> dépile
  }, []);

  // parServeur : la session est déjà refusée par le serveur (inutile de la fermer).
  // Sinon (bouton « Déconnexion », inactivité) : on libère tout de suite la session sur le serveur,
  // pour que l'agent puisse se reconnecter sur un autre appareil.
  const deconnexion = useCallback((options) => {
    const parServeur = !!(options && options.parServeur);
    const ancien = lireStockage(CODE_KEY);
    if (!parServeur && ancien) {
      axios.post(`${API}/mobile/logout`, {}, { headers: entetes(ancien), timeout: 8000 }).catch(() => {});
    }
    ecrireStockage(CODE_KEY, "");
    ecrireStockage(AGENT_KEY, "");
    ecrireStockage(ROLE_KEY, "");
    const profondeur = pileRef.current.length;
    majPile([]);
    if (profondeur) window.history.go(-profondeur);
    setCode("");
    setAgent("");
    setRole("");
  }, [majPile]);

  // Contrôle d'accès toutes les 60 s et au retour au premier plan : hors horaires ou logiciel du bureau
  // fermé, l'application se ferme d'elle-même (retour à l'écran de connexion avec le motif).
  // Agents (pas l'administrateur) : 15 min sans toucher l'écran -> déconnexion, même au retour
  // de l'arrière-plan. Le contrôle signale au serveur si l'écran a été utilisé depuis le précédent.
  useEffect(() => {
    if (!code) return undefined;
    let actif = true;
    // Session Windows (ordinateur du bureau) : pas de limite d'inactivité, seuls les horaires et le
    // logiciel ouvert comptent (contrôlés par le serveur).
    // 2.6.1 : comptes « sans limite » (Moha) : pas de déconnexion après inactivité (réponse du serveur, gardée
    // dans le téléphone pour le retour au premier plan).
    const sansLimite = () => lireStockage(SANS_LIMITE_KEY) === `${code.slice(-16)}:1`;
    const limiteInactivite = role !== "admin" && !sessionWindows(code);
    let derniereAction = Date.now();
    let dernierControle = Date.now();
    const noterAction = () => {
      derniereAction = Date.now();
    };
    const EVENEMENTS = ["pointerdown", "touchstart", "keydown", "wheel", "scroll"];
    EVENEMENTS.forEach((n) => window.addEventListener(n, noterAction, { capture: true, passive: true }));
    const verifierInactivite = () => {
      if (limiteInactivite && !sansLimite() && Date.now() - derniereAction > INACTIVITE_MS) {
        definirMotifDeconnexion(MSG_INACTIVITE);
        deconnexion();
        return true;
      }
      return false;
    };
    const controler = () => {
      if (document.visibilityState === "hidden") return;
      if (verifierInactivite()) return;
      const utilise = derniereAction > dernierControle ? "1" : "0";
      dernierControle = Date.now();
      axios
        .get(`${API}/mobile/acces`, { headers: entetes(code), params: { actif: utilise }, timeout: 10000 })
        .then((r) => ecrireStockage(SANS_LIMITE_KEY, r.data && r.data.sans_limite ? `${code.slice(-16)}:1` : ""))
        .catch((e) => {
          if (actif && estNonAutorise(e)) deconnexion({ parServeur: true });
        });
    };
    const minuteur = window.setInterval(controler, 60000);
    const minuteurInactivite = window.setInterval(verifierInactivite, 15000);
    document.addEventListener("visibilitychange", controler);
    controler(); // 2.6.1 : connaître tout de suite les droits « sans limite »
    return () => {
      actif = false;
      window.clearInterval(minuteur);
      window.clearInterval(minuteurInactivite);
      document.removeEventListener("visibilitychange", controler);
      EVENEMENTS.forEach((n) => window.removeEventListener(n, noterAction, { capture: true }));
    };
  }, [code, role, deconnexion]);

  const ouvrirFiche = useCallback((groupe, id) => ouvrir({ type: "fiche", groupe, id }), [ouvrir]);
  const ouvrirScanner = useCallback(() => ouvrir({ type: "scanner" }), [ouvrir]);
  const ouvrirConsentement = useCallback((personne) => ouvrir({ type: "consentement", personne }), [ouvrir]);
  const ouvrirSignature = useCallback(() => ouvrir({ type: "signature" }), [ouvrir]);
  const ouvrirMessages = useCallback(() => ouvrir({ type: "messages" }), [ouvrir]);
  const ouvrirCaisse = useCallback(() => ouvrir({ type: "caisse" }), [ouvrir]);

  useEffect(() => {
    if (code && ecranDemande.current === "messages") {
      ecranDemande.current = "";
      ouvrirMessages();
    }
  }, [code, ouvrirMessages]);
  const ouvrirConversation = useCallback((numero) => ouvrir({ type: "conversation", numero }), [ouvrir]);

  if (!code) {
    return (
      <EcranLogin
        onOk={(c, a, r) => {
          setCode(c);
          setAgent(a || "");
          setRole(r || "");
        }}
      />
    );
  }

  // Écran visible = le plus haut de la pile qui n'est pas la signature
  let indexVisible = -1;
  pile.forEach((e, i) => {
    if (e.type !== "signature") indexVisible = i;
  });

  return (
    <>
      <div inert={indexVisible >= 0 ? true : undefined} aria-hidden={indexVisible >= 0 ? true : undefined}>
        <EcranRecherche
          code={code}
          agent={agent}
          onLogout={deconnexion}
          onScanner={ouvrirScanner}
          onMessages={ouvrirMessages}
          onCaisse={ouvrirCaisse}
          auPremierPlan={indexVisible < 0}
          onOuvrirFiche={ouvrirFiche}
        />
      </div>
      {pile.map((e, i) => {
        if (e.type === "signature") return null;
        const couvert = i !== indexVisible;
        let contenu = null;
        if (e.type === "fiche") {
          contenu = (
            <EcranFiche
              code={code}
              groupe={e.groupe}
              demandeId={e.id}
              onRetour={retour}
              onOuvrirMembre={ouvrirFiche}
              onConsentement={ouvrirConsentement}
              onLogout={deconnexion}
            />
          );
        } else if (e.type === "messages") {
          contenu = (
            <EcranMessages
              code={code}
              actif={!couvert}
              onRetour={retour}
              onOuvrirConversation={ouvrirConversation}
              onLogout={deconnexion}
            />
          );
        } else if (e.type === "conversation") {
          contenu = (
            <EcranConversation
              code={code}
              numero={e.numero}
              actif={!couvert}
              onRetour={retour}
              onOuvrirFiche={ouvrirFiche}
              onLogout={deconnexion}
            />
          );
        } else if (e.type === "caisse") {
          contenu = <EcranCaisse code={code} onRetour={retour} onLogout={deconnexion} />;
        } else if (e.type === "scanner") {
          contenu = <EcranScanner code={code} onFermer={retour} onLogout={deconnexion} />;
        } else if (e.type === "consentement") {
          contenu = (
            <EcranConsentement
              code={code}
              personne={e.personne}
              signatureOuverte={!!pile[i + 1] && pile[i + 1].type === "signature"}
              onOuvrirSignature={ouvrirSignature}
              onFermerSignature={retour}
              onRetour={retour}
              onLogout={deconnexion}
            />
          );
        }
        return (
          <div
            key={e.cle}
            className="fixed inset-0 bg-[#F4F6F9]"
            style={{ zIndex: 10 + i }}
            inert={couvert ? true : undefined}
            aria-hidden={couvert ? true : undefined}
          >
            {contenu}
          </div>
        );
      })}
    </>
  );
}

export default App;
